// src/components/CsvImportCibleBtn.jsx — bouton + mini-modal d'import CSV
// ciblé sur 1 logement × N mois (vs l'ancien import multi-CSV multi-owners).
//
// Détecte le format (Airbnb / Booking / inconnu) via detectCsvFormat, filtre les
// rows sur le logement courant via resolveListingId AVANT buildImportPlan,
// affiche un aperçu compact puis applique via applyImportPlan + saveData
// (garde commit 17d5acd préservée).
//
// Utilisé dans la sidebar Données (à côté du bouton iCal).

import { useState, useRef, useEffect } from "react"
import { DS } from "../lib/ui"
import { parseAirbnbCsv, buildImportPlan, applyImportPlan } from "../lib/csv-airbnb"
import { parseBookingCsv, buildBookingImportPlan, applyBookingImportPlan, repairMojibake, proposerChambresBooking } from "../lib/csv-booking"
import { resolveListingId, AIRBNB_MAP } from "../lib/airbnb-map"
import { chambresDuGroupeBooking, dateBasculeCsv, moisFactureVoyageur, moisFactureDuCodeV2, MOIS_FACTURES_HORS_APP } from "../lib/csv-bascule"
import { formatMois } from "../lib/format"
import { collectMontantOverrides } from "../lib/data-projection"
import { dejaFactureAilleurs } from "../lib/csv-versements"
import { detectCsvFormat } from "../lib/csv-detect"
import { codesAbsentsDuReleve, texteCodesAbsents, ecartsAvecV2, texteEcartsV2 } from "../lib/csv-codes-absents"
import { getFacturationSource } from "../lib/facturation-source"
// F13 (tour 4, R3) : l'issue du dernier geste — refus certain ou « je ne sais pas ».
import { dernierEchecIncertain, MESSAGE_DATA_ECRITURE_DELAI } from "../lib/data-api"

// F13 (relecture adversariale, tour 2) : après un import refusé, l'écran
// garde encore les réservations de l'import (un geste s'affiche avant
// l'écriture) ; un nouvel essai dans la même fenêtre recalculait le plan sur
// cet écran et concluait à tort « Aucune résa… bon logement ». On demande
// donc de fermer : l'écran se remet sur la base, puis on réessaie.
// Texte VALIDÉ PAR DIMITRI.
export const TEXTE_CSV_APRES_REFUS =
  "Fermez cette fenêtre : l'écran va se remettre à jour, puis réessayez."

// Maison Zen (décision Dimitri 03/10) — textes VALIDÉS PAR DIMITRI.
const fmtJourMois = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—")
export const texteAvertissementV2 = (bascule) => bascule
  ? `Cette chambre ne passe plus par Beds24 depuis le ${fmtJourMois(bascule)}. Les réservations de ce relevé qui arrivent à partir de cette date comptent dans la facturation et les statistiques. Une réservation que Beds24 connaissait déjà (même code) n'est comptée qu'une fois. Les arrivées d'avant le ${fmtJourMois(bascule)} ne changent pas.`
  : "Ce logement facture via la sync Beds24. L'import CSV n'alimentera PAS sa facturation (source V2) — réservé au rattrapage exceptionnel."
export const TEXTE_CHAMBRES_A_VERIFIER =
  "Le relevé Booking ne dit pas toujours la chambre. Vérifie la chambre de chaque réservation avant de confirmer."
// D1 (décision Dimitri 03/10) — texte VALIDÉ ; « (facture déjà émise) »
// remplace « (facture voyageur émise) » (Dimitri, 06/10 : vaut pour V et P).
// ignored[] des plans → « Octobre 2026 — Cocon naturel » par mois facturé.
export function texteMoisFacture(ignored, nomsParId, monthKeyToLabel) {
  const vus = new Map()
  for (const i of ignored || []) {
    const m = /^mois_facture:(\d{4}-\d{2})$/.exec((i && i.reason) || '')
    if (!m) continue
    const cle = m[1] + '|' + (i.logId || '')
    vus.set(cle, (vus.get(cle) || 0) + 1)
  }
  if (vus.size === 0) return null
  const label = typeof monthKeyToLabel === 'function' ? monthKeyToLabel : formatMois
  const morceaux = [...vus].sort(([a], [b]) => a.localeCompare(b)).map(([cle, n]) => {
    const [mk, lid] = cle.split('|')
    return `${label(mk)} — ${(nomsParId && nomsParId[lid]) || lid} (${n} réservation${n > 1 ? 's' : ''})`
  })
  return `Mois déjà facturé (facture déjà émise) : ${morceaux.join(' ; ')}. Ces réservations ne sont pas importées : le mois facturé ne change pas.`
}

// Doublon d'argent (audit) — texte VALIDÉ PAR DIMITRI le 03/10 : une même
// référence Booking sur plusieurs lignes du relevé est additionnée.
const eur = (n) => `${Number(n).toFixed(2).replace('.', ',')} €`
export function texteRegroupements(regroupements) {
  if (!Array.isArray(regroupements) || regroupements.length === 0) return null
  const items = regroupements.map((g) =>
    `${g.bookingRef} : ${g.nets.map(eur).join(' + ')} = ${eur(g.total)}`)
  // Texte validé par Dimitri (03/10), trois lignes ; une ligne par référence.
  return ["Même réservation sur plusieurs lignes du relevé.", "Montants additionnés, une seule réservation.", ...items].join("\n")
}

// Point B (relecture) — texte VALIDÉ PAR DIMITRI : remboursement
// sur une réservation déjà comptée (signe moins typographique « − »).
const eurSigne = (n) => (Number(n) < 0 ? '−' + eur(-Number(n)) : eur(n))
export const texteRemboursement = ({ code, rembourse, compte }) =>
  `${code} : remboursement de ${eurSigne(rembourse)} sur une réservation comptée ${eurSigne(compte)}.`
const texteRemboursements = (liste) =>
  Array.isArray(liste) && liste.length > 0 ? liste.map(texteRemboursement).join("\n") : null

// Point C (relecture) — texte VALIDÉ PAR DIMITRI. Tant que l'écran
// n'a pas confirmé la lecture des factures, « Confirmer » est refusé : une
// liste vide ne vaut pas « aucun mois facturé » (D1 contourné sinon).
export const TEXTE_FACTURES_PAS_LUES =
  "Factures pas encore lues : import en attente. Réessaie dans un instant."

// Montants groupés par milliers (« 1 393,10 € »), signe moins typographique.
const eurMilliers = (n) => {
  const [ent, dec] = Math.abs(Number(n) || 0).toFixed(2).split('.')
  return `${Number(n) < 0 ? '−' : ''}${ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${dec} €`
}
// Relecture tour 3 — textes VALIDÉS PAR DIMITRI (06/10).
// Point 2 : correction à la main gardée comme une part.
export const texteCorrectionGardee = ({ code, ecart, total }) =>
  `${code} : ta correction à la main (${eurMilliers(ecart)}) est gardée. Montant après import : ${eurMilliers(total)}.`
// Point 3 : ligne sans identifiant de versement face à une ligne détaillée.
// Tour 5 : « sans date de versement » → « sans versement identifiable » (la
// date n'est plus l'identifiant) — texte VALIDÉ PAR DIMITRI.
export const texteSansIdentifiant = (code) =>
  `${code} : ligne du relevé sans versement identifiable, refusée (cette réservation est déjà détaillée par versements). Montant inchangé.`
const texteSansIdentifiants = (ignored) => {
  const codes = (ignored || []).filter((i) => i && i.reason === 'versement_sans_identifiant')
    .map((i) => i.airbnbCode || i.bookingRef).filter(Boolean)
  return codes.length > 0 ? codes.map(texteSansIdentifiant).join("\n") : null
}

// Relecture tour 4 — textes VALIDÉS PAR DIMITRI (06/10).
// Refus nommés : versement déjà facturé ailleurs, correction + remboursement
// à trancher, mois facturé hors de l'application.
export function texteRefusNomme(i, nomsParId) {
  const code = (i && (i.airbnbCode || i.bookingRef)) || '—'
  const reason = (i && i.reason) || ''
  let m = /^deja_facture:(\d{4}-\d{2}):(.*)$/.exec(reason)
  if (m) return `${code} : déjà facturé en ${formatMois(m[1])}, ${m[2]}. Import refusé.`
  // Tour 5 — texte VALIDÉ PAR DIMITRI : même séjour et même montant
  // qu'une réservation facturée, mais prénom différent.
  m = /^a_trancher_chevauchement:(\d{4}-\d{2}):(.*)$/.exec(reason)
  if (m) return texteChevauchement(code, formatMois(m[1]), m[2])
  m = /^a_trancher_deja_facture:(\d{4}-\d{2}):(.*)$/.exec(reason)
  if (m) return `${code} : même période et même montant qu'une réservation déjà facturée en ${formatMois(m[1])} (${m[2]}), mais le prénom diffère. À trancher : import refusé.`
  if (reason === 'avant_suivi_versements') return texteAvantSuivi(code, i.versements)
  if (reason === 'a_trancher_correction_remboursement')
    return `${code} : à trancher : correction à la main et remboursement. Import refusé, montant inchangé.`
  m = /^mois_facture_hors_app:(\d{4}-\d{2})$/.exec(reason)
  if (m) {
    const nom = (nomsParId && nomsParId[i.logId]) || i.logId
    return `${code} : ${formatMois(m[1])} — ${nom}, facturé hors de l'application (${MOIS_FACTURES_HORS_APP[m[1]] || '?'}). Import refusé.`
  }
  return null
}
const texteRefusNommes = (ignored, nomsParId) => {
  const lignes = (ignored || []).map((i) => texteRefusNomme(i, nomsParId)).filter(Boolean)
  return lignes.length > 0 ? lignes.join("\n") : null
}
// Décision Dimitri 06/10 (« ok refus », remplace la règle 3b) — texte VALIDÉ
// PAR DIMITRI (06/10), au singulier et au pluriel, mot pour mot.
const jourFr = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : 'date inconnue')
export function texteAvantSuivi(code, versements) {
  const v = Array.isArray(versements) ? versements : []
  const items = v.map((x) => `du ${jourFr(x && x.date)} (${eurMilliers(x && x.montant)})`)
  const versementTxt = items.length > 1
    ? `Versements ${items.slice(0, -1).join(', ')} et ${items[items.length - 1]} refusés`
    : `Versement ${items[0] || 'du relevé'} refusé`
  return `${code} : réservation importée avant le suivi des versements. ${versementTxt} : impossible de savoir s'il est déjà compté. Rien n'est ajouté.`
}
// Tour 6 — texte VALIDÉ PAR DIMITRI :
// séjour à cheval sur un mois facturé, montant différent.
export const texteChevauchement = (code, mois, numero) =>
  `${code} : ce séjour chevauche une réservation déjà facturée en ${mois} (${numero}), avec un montant différent. À trancher : import refusé.`
// Ménage corrigé à la main gardé (point 4).
export const texteMenageGarde = ({ code, ecartMenage, menage }) =>
  `${code} : ton ménage corrigé à la main (${eurMilliers(ecartMenage)}) est gardé. Ménage après import : ${eurMilliers(menage)}.`
// Délai maximal de la relecture des factures à la confirmation (tour 4).
const DELAI_RELECTURE_FACTURES_MS = 15000

const RAISON_PROPOSITION = {
  releve: "lue dans le relevé",
  beds24: "proposée : vue dans Beds24",
  deja_importee: "proposée : déjà importée ici",
  calendrier: "proposée : arrivée vue au calendrier",
  chambre_ouverte: "proposée : chambre ouverte",
}

// Nom affiché d'une chambre : prop `chambres` si l'écran la donne, sinon le nom
// de l'annonce (carte des annonces), sinon l'identifiant.
function nomsDesChambres(groupe, log, chambres) {
  const out = {}
  for (const lid of groupe) {
    const c = Array.isArray(chambres) ? chambres.find((x) => x && x.id === lid) : null
    const cle = Object.keys(AIRBNB_MAP).find((k) => AIRBNB_MAP[k] === lid)
    out[lid] = (c && c.nom) || (lid === log.id && log.nom) ||
      (cle ? cle.charAt(0).toUpperCase() + cle.slice(1) : lid)
  }
  return out
}

export default function CsvImportCibleBtn({ log, month, data, saveData, onRelire, chambres, invoicesRaw, monthKeyToLabel, resasByLogement, invoicesLoaded, relireFactures }) {
  // Point C : seul un `true` explicite vaut « factures lues » (fail-closed).
  const facturesLues = invoicesLoaded === true
  const [modalOpen, setModalOpen] = useState(false)
  const [plan, setPlan] = useState(null)
  const [planType, setPlanType] = useState(null) // 'airbnb' | 'booking'
  const [formatMsg, setFormatMsg] = useState(null) // { type: 'unknown', text: '...' }
  const [menageInfo, setMenageInfo] = useState(null) // { type: 'info'|'warn', text: '...' } — Booking only
  const [parseWarning, setParseWarning] = useState(null) // { type: 'info', text: '...' } — warning non bloquant du parser
  const [error, setError] = useState(null)
  const [applying, setApplying] = useState(false)
  const [done, setDone] = useState(null)
  const fileInputRef = useRef(null)
  // Un import a échoué dans cette fenêtre : plus de nouvel essai ici.
  // false | 'refus' (refus certain : « Fermez… réessayez ») | 'incertain'
  // (on ne sait pas s'il est passé : « je ne sais pas… rechargez »).
  const [aFermer, setAFermer] = useState(false)
  // Verrou SYNCHRONE : deux clics dans le même instant (avant que le bouton
  // désactivé ne s'affiche) n'envoient qu'un enregistrement.
  const applicationEnCours = useRef(false)
  // Maison Zen : une chambre par ligne du relevé Booking (choix de Dimitri du
  // 03/10 : « Confirmer l'import » vaut validation des chambres affichées,
  // les proposées restent signalées « proposée : … »). `bookingCtx` garde de
  // quoi recalculer le plan quand Dimitri change une chambre ; `choixChambres`
  // = { [référence]: id chambre } ; `propositions` = { [référence]: {lid,lu,raison} }.
  const [bookingCtx, setBookingCtx] = useState(null)
  const [choixChambres, setChoixChambres] = useState({})
  const [propositions, setPropositions] = useState({})
  // Lignes du relevé lu + plateforme : de quoi signaler les codes déjà
  // importés absents de ce relevé (texte seul, rien n'est supprimé).
  const [releve, setReleve] = useState(null)
  // D1 : import entièrement refusé (tous les mois du relevé sont facturés).
  const [refusMois, setRefusMois] = useState(null)
  // Point B : relevé sans rien à importer mais qui rembourse une réservation
  // déjà comptée — on le DIT (jamais « aucune résa »).
  const [remboursementsSeuls, setRemboursementsSeuls] = useState(null)
  // Tour 3 point 3 : lignes sans identifiant refusées, rien d'autre à importer.
  const [sansIdSeuls, setSansIdSeuls] = useState(null)
  // Tour 4 : refus nommés (déjà facturé, à trancher, hors app), rien d'autre.
  const [refusNommesSeuls, setRefusNommesSeuls] = useState(null)
  // D1 : (logement, mois) déjà facturé → import refusé sur ce mois.
  const moisGele = moisFactureVoyageur(invoicesRaw, monthKeyToLabel || formatMois)
  // D : la V2 du même code rangée dans un mois facturé → import refusé aussi.
  const codeGeleV2 = moisFactureDuCodeV2(resasByLogement, moisGele, data)

  // WAGON M (M3.5) : un logement en source de facturation 'v2' (sync Beds24).
  // Maison Zen (03/10) : une chambre avec une date de bascule compte les lignes
  // CSV arrivées à partir de cette date (src/lib/csv-bascule.js).
  const isV2Source = getFacturationSource(log) === "v2"
  const basculeCsv = isV2Source ? dateBasculeCsv(log.id) : null
  const groupeBooking = chambresDuGroupeBooking(log.id)

  const reset = () => {
    setPlan(null); setPlanType(null); setFormatMsg(null); setMenageInfo(null); setParseWarning(null)
    setError(null); setDone(null); setApplying(false); setAFermer(false)
    setBookingCtx(null); setChoixChambres({}); setPropositions({}); setReleve(null); setRefusMois(null); setRemboursementsSeuls(null); setSansIdSeuls(null); setRefusNommesSeuls(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }
  // F13 (tour 3) : fermer après un refus RELIT les données (la relecture
  // automatique, qui n'écrit rien) — c'est ce qui rend vraie la phrase
  // « l'écran va se remettre à jour ». La relecture part APRÈS que la fenêtre
  // a disparu de l'écran (effet ci-dessous) : ouverte, elle compterait comme
  // « saisie en cours » et rien ne serait adopté.
  const [relireApresFermeture, setRelireApresFermeture] = useState(false)
  useEffect(() => {
    if (!relireApresFermeture || modalOpen) return
    setRelireApresFermeture(false)
    if (typeof onRelire === "function") {
      Promise.resolve(onRelire()).catch((e) => console.error("[CsvImportCibleBtn] relecture après refus", e))
    }
  }, [relireApresFermeture, modalOpen, onRelire])
  const close = () => {
    if (aFermer) setRelireApresFermeture(true)
    reset(); setModalOpen(false)
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    reset()
    try {
      const rawText = await file.text()
      // Dé-mojibake EN AMONT : un export Booking FR peut arriver en UTF-8 lu
      // comme Latin-1 (« NumÃ©ro de rÃ©servation »). On répare avant la
      // détection ET le parsing pour que tout le pipeline voie du texte propre.
      // No-op si le fichier est déjà propre (Airbnb, Booking EN, FR clean).
      const text = repairMojibake(rawText)
      const firstLine = (text || '').split(/\r?\n/)[0] || ''
      const format = detectCsvFormat(firstLine)
      if (format === 'unknown') {
        setFormatMsg({ type: 'unknown', text: "Format non reconnu. Seuls Airbnb et Booking sont supportés pour le moment." })
        return
      }
      if (format === 'airbnb') {
        const { rows, warnings } = parseAirbnbCsv(text)
        if (warnings.some(w => w.reason && w.reason.startsWith('header_invalid'))) {
          setError("Le header Airbnb attendu n'a pas été trouvé. Fichier CSV corrompu ou format Airbnb modifié ?")
          return
        }
        // Filtrage AVANT buildImportPlan : ne garder que les Réservations de CE logement.
        const rowsForLog = rows.filter(r =>
          r.type !== 'Réservation' || (r.logement && resolveListingId(r.logement) === log.id)
        )
        const p = buildImportPlan(data, rowsForLog, { v2Lids: isV2Source ? [log.id] : [], moisGele, codeGeleV2 })
        if (p.toCreate.length === 0 && p.toUpdate.length === 0) {
          const refus = texteMoisFacture(p.ignored, { [log.id]: log.nom }, monthKeyToLabel)
          const remb = texteRemboursements(p.remboursements)
          const sansId = texteSansIdentifiants(p.ignored)
          const nommes = texteRefusNommes(p.ignored, { [log.id]: log.nom })
          if (refus || remb || sansId || nommes) { setRefusMois(refus); setRemboursementsSeuls(remb); setSansIdSeuls(sansId); setRefusNommesSeuls(nommes); return }
          setError(`Aucune résa Airbnb pour "${log.nom}" dans ce CSV. Vérifier que le fichier correspond au bon propriétaire / logement.`)
          return
        }
        setPlan(p); setPlanType('airbnb'); setReleve({ rows, plateforme: 'airbnb' })
        return
      }
      // Format Booking
      const { rows, warnings } = parseBookingCsv(text)
      if (warnings.some(w => w.reason && w.reason.startsWith('header_invalid'))) {
        setError("Le header Booking attendu n'a pas été trouvé. Fichier CSV corrompu ou format Booking modifié ?")
        return
      }
      // Warnings non bloquants (cas A et cas B) — hétérogénéité Tourism tax par ligne.
      const headerIncompletWarn = warnings.find(w => w.reason && w.reason.startsWith('header_incomplet_tourism_tax_decalee'))
      const lignesSansWarn = warnings.find(w => w.reason && w.reason.startsWith('lignes_sans_tourism_tax_decalees'))
      if (headerIncompletWarn) {
        const m = headerIncompletWarn.reason.match(/\((\d+) ligne/)
        const count = m ? m[1] : '?'
        setParseWarning({ type: 'info', text: `ℹ️ ${count} ligne(s) Booking avec Tourism tax dans un header incomplet détectée(s) et corrigée(s) automatiquement.` })
      } else if (lignesSansWarn) {
        const m = lignesSansWarn.reason.match(/\((\d+) ligne/)
        const count = m ? m[1] : '?'
        setParseWarning({ type: 'info', text: `ℹ️ ${count} ligne(s) Booking sans Tourism tax dans un header complet détectée(s) et corrigée(s) automatiquement.` })
      }
      const menageForfait = Number(log.tarifMenage) || 0
      let p
      if (groupeBooking) {
        // Maison Zen : chaque ligne va dans SA chambre — lue dans le relevé,
        // sinon proposée ; rien ne s'enregistre sans validation de Dimitri.
        const props = proposerChambresBooking(data, rows, {
          groupe: groupeBooking,
          chambreOuverte: log.id,
          nomsParId: nomsDesChambres(groupeBooking, log, chambres),
          resasByLogement,
        })
        const propsObj = Object.fromEntries(props)
        const choix = Object.fromEntries([...props].map(([ref, x]) => [ref, x.lid]))
        const v2Lids = groupeBooking.filter((lid) => dateBasculeCsv(lid) || (lid === log.id && isV2Source))
        const ctx = { rows, menageForfait, v2Lids }
        p = planBookingPourChoix(ctx, choix)
        setBookingCtx(ctx); setPropositions(propsObj); setChoixChambres(choix)
      } else {
        p = buildBookingImportPlan(data, rows, log.id, { menageForfait, moisGele, codeGeleV2 })
      }
      if (p.toCreate.length === 0 && p.toUpdate.length === 0) {
        const refus = texteMoisFacture(p.ignored,
          groupeBooking ? nomsDesChambres(groupeBooking, log, chambres) : { [log.id]: log.nom }, monthKeyToLabel)
        const remb = texteRemboursements(p.remboursements)
        const sansId = texteSansIdentifiants(p.ignored)
        const nommes = texteRefusNommes(p.ignored,
          groupeBooking ? nomsDesChambres(groupeBooking, log, chambres) : { [log.id]: log.nom })
        if (refus || remb || sansId || nommes) { setRefusMois(refus); setRemboursementsSeuls(remb); setSansIdSeuls(sansId); setRefusNommesSeuls(nommes); return }
        setError(`Aucune résa Booking pour "${log.nom}" dans ce CSV. Vérifier que le fichier correspond au bon logement.`)
        return
      }
      setPlan(p); setPlanType('booking'); setReleve({ rows, plateforme: 'booking' })
      setMenageInfo(menageForfait > 0
        ? { type: 'info', text: `ℹ️ Ménage appliqué automatiquement : ${menageForfait} €/résa (Tarif ménage du logement).` }
        : { type: 'warn', text: "⚠️ Ménage forfait non défini pour ce logement. Les résas auront menage=0. Tu peux saisir le forfait dans Paramètres → 🧹 Tarif ménage pour qu'il soit appliqué automatiquement." }
      )
    } catch (err) {
      setError(`Erreur de lecture : ${err.message}`)
    }
  }

  // Maison Zen : plan Booking recalculé pour un choix de chambres donné.
  function planBookingPourChoix(ctx, choix) {
    return buildBookingImportPlan(data, ctx.rows, (r) => choix[r.bookingRef] || null, {
      menageForfait: ctx.menageForfait,
      v2Lids: ctx.v2Lids,
      chambresGroupe: groupeBooking,
      moisGele,
      codeGeleV2,
    })
  }
  const changerChambre = (ref, lid) => {
    if (!bookingCtx) return
    const choix = { ...choixChambres, [ref]: lid }
    setChoixChambres(choix)
    setPlan(planBookingPourChoix(bookingCtx, choix))
  }

  // Relecture argent (F13 suite) : un plan dont l'enregistrement a échoué
  // n'est JAMAIS rejoué. Il a été calculé sur l'écran d'avant ; le jeton n'a
  // pas bougé, donc un second clic passerait et recréerait les mêmes
  // réservations avec de nouveaux identifiants (CA et facture doublés). On le
  // retire : le fichier se rechoisit, et le plan se recalcule.
  const abandonnerPlan = (message, incertain = false) => {
    setPlan(null); setPlanType(null); setMenageInfo(null); setParseWarning(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    setError(message)
    setAFermer(incertain ? 'incertain' : 'refus')
  }

  const handleApply = async () => {
    if (!plan || applicationEnCours.current) return
    if (!facturesLues) { setError(TEXTE_FACTURES_PAS_LUES); return }
    applicationEnCours.current = true
    setApplying(true); setError(null)
    // Relecture tour 3 (point 5) — onglet périmé : les factures sont RELUES
    // juste avant d'enregistrer. Une facture émise depuis l'ouverture de
    // l'écran gèle son mois → refus. Relecture ratée (ou impossible) → refus.
    let facturesFraiches
    try {
      if (typeof relireFactures !== 'function') throw new Error('relecture des factures indisponible')
      // Tour 4 (point 5) : 15 s au plus ; au-delà, refus.
      let minuteur
      const delai = new Promise((_, ko) => {
        minuteur = setTimeout(() => ko(new Error('relecture des factures trop lente')), DELAI_RELECTURE_FACTURES_MS)
      })
      try {
        facturesFraiches = await Promise.race([relireFactures(), delai])
      } finally {
        clearTimeout(minuteur)
      }
      if (!Array.isArray(facturesFraiches)) throw new Error('relecture des factures sans résultat')
    } catch (e) {
      console.error('[CsvImportCibleBtn] relecture des factures', e)
      setError(TEXTE_FACTURES_PAS_LUES)
      applicationEnCours.current = false
      setApplying(false)
      return
    }
    {
      const geleFrais = moisFactureVoyageur(facturesFraiches, monthKeyToLabel || formatMois)
      const codeGeleFrais = moisFactureDuCodeV2(resasByLogement, geleFrais, data)
      const bloquees = [
        ...plan.toCreate.map((c) => ({ logId: c.logId, mk: c.moisEffectif || c.month, code: c.resa && (c.resa.airbnbCode || c.resa.bookingRef), garde: c.gardeDejaFacture })),
        ...plan.toUpdate.map((u) => ({ logId: u.logId, mk: u.moisEffectif || u.month, code: u.airbnbCode || u.bookingRef, garde: u.gardeDejaFacture })),
      ].map((x) => {
        if (geleFrais(x.logId, x.mk)) return { reason: 'mois_facture:' + x.mk, logId: x.logId }
        const v2 = codeGeleFrais(x.code)
        if (v2) return { reason: 'mois_facture:' + v2.mk, logId: v2.lid }
        // Tour 5 (point 5) : garde « déjà facturé ailleurs » refaite avec les
        // factures relues (même règle que le plan).
        const df = x.garde ? dejaFactureAilleurs(data, x.logId, x.garde, geleFrais) : null
        if (df) {
          const motif = df.motif || 'deja_facture'
          return { reason: motif + ':' + df.mk + ':' + (df.numero || '?'), logId: x.logId,
            [planType === 'booking' ? 'bookingRef' : 'airbnbCode']: x.code }
        }
        return null
      }).filter(Boolean)
      if (bloquees.length > 0) {
        applicationEnCours.current = false
        setApplying(false)
        const noms = groupeBooking ? nomsChambres : { [log.id]: log.nom }
        const textes = [texteMoisFacture(bloquees, noms, monthKeyToLabel), texteRefusNommes(bloquees, noms)].filter(Boolean)
        abandonnerPlan('Import refusé. ' + textes.join(' '))
        return
      }
    }
    try {
      // Origine CSV + date d'import sur chaque création / mise à jour.
      const importeLe = new Date().toISOString()
      const newData = planType === 'booking'
        ? applyBookingImportPlan(data, plan, { importeLe })
        : applyImportPlan(data, plan, { importeLe })
      // F13 : `saveData` ne lève pas — il rend `false` sur un refus (409,
      // garde, 503, délai) et montre déjà le bandeau d'écriture. Le résultat
      // ne s'affiche donc QUE sur un enregistrement confirmé (texte à faire
      // valider par Dimitri).
      const ok = await saveData(newData)
      if (ok !== true) {
        abandonnerPlan("L'enregistrement de l'import n'a pas été confirmé — voir le message en haut de l'écran.", dernierEchecIncertain())
        return
      }
      setDone({
        created: plan.toCreate.length,
        updated: plan.toUpdate.length,
        ignored: plan.ignored.length,
      })
    } catch (err) {
      abandonnerPlan(`Erreur pendant l'import : ${err.message}`)
    } finally {
      applicationEnCours.current = false
      setApplying(false)
    }
  }

  // Résumé cas d'attention compact
  const overwriting = plan ? plan.toUpdate.filter(u => u.overwritingManual) : []
  // WAGON M (M3.5) : sans date de bascule, un logement 'v2' ne tire PAS sa
  // facturation du CSV — on avertit AVANT import (texteAvertissementV2).
  // Maison Zen : lignes du relevé Booking à rattacher à une chambre (celles
  // qui s'importent, ou qu'un choix de chambre a mises de côté).
  const lignesChambre = (plan && planType === 'booking' && bookingCtx) ? (() => {
    const refs = new Set([
      ...plan.toCreate.map(c => c.resa.bookingRef),
      ...plan.toUpdate.map(u => u.bookingRef),
      ...plan.ignored.filter(i => /^(deja_dans_une_autre_chambre|chambre_non_choisie)/.test(i.reason || '')).map(i => i.bookingRef),
    ])
    return bookingCtx.rows.filter(r => r && r.bookingRef && refs.has(r.bookingRef) && propositions[r.bookingRef])
      .filter((r, i, arr) => arr.findIndex(x => x.bookingRef === r.bookingRef) === i)
  })() : []
  const chambresAValider = lignesChambre.some(r => !propositions[r.bookingRef].lu)
  const nomsChambres = groupeBooking ? nomsDesChambres(groupeBooking, log, chambres) : {}
  const ignoreeAutreChambre = (ref) => {
    const i = plan && plan.ignored.find(x => x.bookingRef === ref && /^deja_dans_une_autre_chambre:/.test(x.reason || ''))
    return i ? i.reason.split(':')[1] : null
  }
  // D1 : mois déjà facturés, lignes refusées (le reste s'importe).
  const texteFacture = plan ? texteMoisFacture(plan.ignored,
    groupeBooking ? nomsChambres : { [log.id]: log.nom }, monthKeyToLabel) : null
  // Tour 3 : corrections à la main gardées, lignes sans identifiant refusées.
  const texteCorrections = plan && Array.isArray(plan.corrections) && plan.corrections.length > 0
    ? (plan.corrections.filter((c) => Math.abs(Number(c.ecart) || 0) > 0.01).map(texteCorrectionGardee).join("\n") || null) : null
  const texteSansId = plan ? texteSansIdentifiants(plan.ignored) : null
  const texteRefusNommesPlan = plan ? texteRefusNommes(plan.ignored,
    groupeBooking ? nomsChambres : { [log.id]: log.nom }) : null
  const texteMenages = plan && Array.isArray(plan.corrections)
    ? (plan.corrections.filter((c) => c.ecartMenage != null).map(texteMenageGarde).join("\n") || null) : null
  // Point B : remboursements sur des réservations déjà comptées.
  const texteRemb = plan ? texteRemboursements(plan.remboursements) : null
  // Références Booking présentes sur plusieurs lignes : additionnées.
  const texteRegroupe = plan ? texteRegroupements(plan.regroupements) : null
  // D2 : le relevé fait foi ; un net différent de Beds24 est signalé.
  // Relecture H : corrections de net posées par code (même calque que l'écran).
  const texteEcarts = plan ? texteEcartsV2(ecartsAvecV2(plan, resasByLogement, {
      corrections: collectMontantOverrides(data, groupeBooking || [log.id]),
    }),
    groupeBooking ? nomsChambres : { [log.id]: log.nom }) : null
  // Codes déjà importés, absents de ce relevé, dans les mois qu'il couvre.
  const texteAbsents = (plan && releve) ? texteCodesAbsents(
    codesAbsentsDuReleve(data, {
      rows: releve.rows,
      plan,
      lids: releve.plateforme === 'booking' && groupeBooking ? groupeBooking : [log.id],
      plateforme: releve.plateforme,
      resasByLogement,
    }),
    groupeBooking ? nomsChambres : { [log.id]: log.nom },
  ) : null
  // Mois concernés par les créations/MAJ (utile en M2 "tous mois du log")
  const monthsConcerned = plan ? (() => {
    const set = new Set()
    plan.toCreate.forEach(c => set.add(c.month))
    plan.toUpdate.forEach(u => set.add(u.month))
    return [...set].sort()
  })() : []

  return (
    <>
      <button onClick={() => setModalOpen(true)}
        style={{background:"#e8f0ff",color:DS.navyLight,border:"none",borderRadius:7,padding:"6px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:DS.fontBody,whiteSpace:"nowrap"}}>
        📎 CSV
      </button>
      {modalOpen && (
        // F13 — repère « saisie en cours sur l'écran Argent » : tant que cette
        // fenêtre est ouverte, un rafraîchissement n'adopte pas de données
        // plus récentes sous son plan d'import (calculé sur `data` à la
        // lecture du fichier) ; il montre le bandeau à la place.
        <div data-saisie-argent="csv" style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",paddingTop:20}}>
          <div style={{background:DS.white,borderRadius:20,padding:20,width:"100%",maxWidth:420,maxHeight:"90vh",overflowY:"auto",fontFamily:DS.fontBody}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div>
                <div style={{fontFamily:DS.fontDisplay,fontSize:15,fontWeight:700,color:DS.navy}}>Import CSV — {log.nom}</div>
                <div style={{fontSize:10,color:"#888",marginTop:2}}>Sélectionne un CSV Airbnb ou Booking exporté depuis l'extranet. Toutes les résas seront importées pour ce logement (tous mois).</div>
              </div>
              <button onClick={close} style={{background:"none",border:"none",fontSize:24,cursor:"pointer",color:"#aaa",lineHeight:1}}>×</button>
            </div>

            {/* WAGON M (M3.5) — avertissement source 'v2' : la facturation de ce
                logement vient de la sync Beds24, pas du CSV. */}
            {isV2Source && !done && (
              <div style={{background:'#fff3e0',border:`1px solid ${DS.amber}55`,borderRadius:10,padding:12,marginBottom:12,fontSize:11,color:"#555",lineHeight:1.5}}>
                ⚠️ {texteAvertissementV2(basculeCsv)}
              </div>
            )}

            {/* Input file — toujours visible tant que pas encore de plan/done */}
            {aFermer && !plan && !done && (
              <div data-testid="csv-apres-refus" style={{marginBottom:12,fontSize:12,color:DS.navy,fontWeight:600,lineHeight:1.5}}>
                {aFermer === 'incertain' ? MESSAGE_DATA_ECRITURE_DELAI : TEXTE_CSV_APRES_REFUS}
              </div>
            )}
            {!aFermer && !plan && !done && (
              <div style={{marginBottom:12}}>
                <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange}
                  style={{display:"block",width:"100%",padding:"8px 10px",border:"1px dashed #cbd5e1",borderRadius:8,background:"#f8fafd",fontSize:12,fontFamily:DS.fontBody}} />
              </div>
            )}

            {/* D1 : tout le relevé tombe sur des mois déjà facturés → refus. */}
            {refusMois && !plan && !done && (
              <div data-testid="csv-mois-facture" style={{background:"#fdf2f2",border:`1px solid ${DS.red}44`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:DS.red,fontWeight:600,lineHeight:1.5}}>
                Import refusé. {refusMois}
              </div>
            )}

            {/* Point B : remboursement d'une réservation déjà comptée, rien d'autre à importer. */}
            {remboursementsSeuls && !plan && !done && (
              <div data-testid="csv-remboursements" style={{background:"#fff3e0",border:`1px solid ${DS.amber}55`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5,whiteSpace:"pre-line"}}>
                {remboursementsSeuls}
              </div>
            )}

            {/* Tour 4 : seulement des refus nommés. */}
            {refusNommesSeuls && !plan && !done && (
              <div data-testid="csv-refus-nommes" style={{background:"#fdf2f2",border:`1px solid ${DS.red}44`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:DS.red,fontWeight:600,lineHeight:1.5,whiteSpace:"pre-line"}}>
                {refusNommesSeuls}
              </div>
            )}

            {/* Tour 3 point 3 : seulement des lignes sans identifiant refusées. */}
            {sansIdSeuls && !plan && !done && (
              <div data-testid="csv-sans-identifiant" style={{background:"#fff3e0",border:`1px solid ${DS.amber}55`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5,whiteSpace:"pre-line"}}>
                {sansIdSeuls}
              </div>
            )}

            {/* Message format non supporté */}
            {formatMsg && (
              <div style={{background:'#fff3e0',border:`1px solid ${DS.amber}55`,borderRadius:10,padding:12,marginBottom:12,fontSize:11,color:"#555",lineHeight:1.5}}>
                ⚠️ {formatMsg.text}
              </div>
            )}

            {/* Plan preview */}
            {plan && !done && (
              <>
                {/* Warning non bloquant du parser (ex. header Booking buggé corrigé auto) */}
                {parseWarning && (
                  <div style={{background:"#eef2f7",border:"1px solid #cbd5e1",borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5}}>
                    {parseWarning.text}
                  </div>
                )}
                {/* Info ménage (Booking uniquement — menageForfait appliqué auto ou non défini) */}
                {menageInfo && (
                  <div style={{background:menageInfo.type==='info'?'#eef2f7':'#fff3e0',border:`1px solid ${menageInfo.type==='info'?'#cbd5e1':DS.amber+'55'}`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5}}>
                    {menageInfo.text}
                  </div>
                )}
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
                  {[
                    ['Créations', plan.toCreate.length, DS.skyDeep],
                    ['Mises à jour', plan.toUpdate.length, DS.amber],
                    ['Ignorées', plan.ignored.length, '#888'],
                  ].map(([label, val, color]) => (
                    <div key={label} style={{background:"#f5f8fc",borderRadius:10,padding:"10px 8px",textAlign:"center"}}>
                      <div style={{fontFamily:DS.fontDisplay,fontSize:22,fontWeight:700,color}}>{val}</div>
                      <div style={{fontSize:9,color:"#888",marginTop:2,fontWeight:600,textTransform:"uppercase",letterSpacing:.3}}>{label}</div>
                    </div>
                  ))}
                </div>
                {monthsConcerned.length > 0 && (
                  <div style={{fontSize:11,color:"#555",marginBottom:10}}>
                    {plan.toCreate.length + plan.toUpdate.length} résa{plan.toCreate.length + plan.toUpdate.length > 1 ? 's' : ''} réparties sur <strong>{monthsConcerned.length} mois</strong> : {monthsConcerned.join(', ')}
                  </div>
                )}
                {/* D1 : lignes refusées car leur mois est déjà facturé. */}
                {texteFacture && (
                  <div data-testid="csv-mois-facture" style={{background:"#fdf2f2",border:`1px solid ${DS.red}44`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:DS.red,fontWeight:600,lineHeight:1.5}}>
                    {texteFacture}
                  </div>
                )}
                {/* Tour 3 point 2 : correction à la main gardée. */}
                {texteCorrections && (
                  <div data-testid="csv-corrections" style={{background:"#fff3e0",border:`1px solid ${DS.amber}55`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5,whiteSpace:"pre-line"}}>
                    {texteCorrections}
                  </div>
                )}
                {/* Tour 4 : ménage corrigé gardé ; refus nommés. */}
                {texteMenages && (
                  <div data-testid="csv-menages" style={{background:"#fff3e0",border:`1px solid ${DS.amber}55`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5,whiteSpace:"pre-line"}}>
                    {texteMenages}
                  </div>
                )}
                {texteRefusNommesPlan && (
                  <div data-testid="csv-refus-nommes" style={{background:"#fdf2f2",border:`1px solid ${DS.red}44`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:DS.red,fontWeight:600,lineHeight:1.5,whiteSpace:"pre-line"}}>
                    {texteRefusNommesPlan}
                  </div>
                )}
                {/* Tour 3 point 3 : ligne sans identifiant refusée. */}
                {texteSansId && (
                  <div data-testid="csv-sans-identifiant" style={{background:"#fff3e0",border:`1px solid ${DS.amber}55`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5,whiteSpace:"pre-line"}}>
                    {texteSansId}
                  </div>
                )}
                {/* Point B : remboursement d'une réservation déjà comptée. */}
                {texteRemb && (
                  <div data-testid="csv-remboursements" style={{background:"#fff3e0",border:`1px solid ${DS.amber}55`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5,whiteSpace:"pre-line"}}>
                    {texteRemb}
                  </div>
                )}
                {/* Même référence sur plusieurs lignes : montants additionnés. */}
                {texteRegroupe && (
                  <div data-testid="csv-regroupements" style={{background:"#eef2f7",border:"1px solid #cbd5e1",borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5,whiteSpace:"pre-line"}}>
                    {texteRegroupe}
                  </div>
                )}
                {/* D2 : écart de montant avec Beds24 — le relevé compte, une fois. */}
                {texteEcarts && (
                  <div data-testid="csv-ecarts-v2" style={{background:"#fff3e0",border:`1px solid ${DS.amber}55`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5}}>
                    {texteEcarts}
                  </div>
                )}
                {/* Codes déjà importés absents de ce relevé : signalés, jamais supprimés. */}
                {texteAbsents && (
                  <div data-testid="csv-codes-absents" style={{background:"#fff3e0",border:`1px solid ${DS.amber}55`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5}}>
                    {texteAbsents}
                  </div>
                )}
                {/* Maison Zen : la chambre de chaque réservation Booking. */}
                {lignesChambre.length > 0 && (
                  <div data-testid="csv-chambres" style={{background:"#f5f8fc",borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555"}}>
                    {chambresAValider && <div style={{marginBottom:8,fontWeight:600,color:DS.navy}}>{TEXTE_CHAMBRES_A_VERIFIER}</div>}
                    {lignesChambre.map(r => {
                      const prop = propositions[r.bookingRef]
                      const autre = ignoreeAutreChambre(r.bookingRef)
                      return (
                        <div key={r.bookingRef} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6,padding:"4px 0",borderTop:"1px solid #e8eef5"}}>
                          <div style={{minWidth:0}}>
                            <div style={{fontWeight:600,color:DS.navy}}>{fmtJourMois(r.checkIn).slice(0,5)} → {fmtJourMois(r.checkOut).slice(0,5)} · {r.guestName || "—"}</div>
                            <div style={{fontSize:10,color:"#888"}}>
                              {autre ? `déjà rangée dans ${nomsChambres[autre] || autre} : pas importée` : RAISON_PROPOSITION[prop.raison]}
                            </div>
                          </div>
                          <select aria-label={`Chambre de la réservation ${r.bookingRef}`} value={choixChambres[r.bookingRef] || ""} disabled={applying}
                            onChange={e => changerChambre(r.bookingRef, e.target.value)}
                            style={{fontSize:11,padding:"4px 6px",borderRadius:6,border:"1px solid #cbd5e1",fontFamily:DS.fontBody,maxWidth:150}}>
                            {groupeBooking.map(lid => <option key={lid} value={lid}>{nomsChambres[lid]}</option>)}
                          </select>
                        </div>
                      )
                    })}
                  </div>
                )}
                {overwriting.length > 0 && (
                  <div style={{background:"#fff3e0",border:`1px solid ${DS.amber}55`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555"}}>
                    ⚠️ <strong>{overwriting.length} écrasement{overwriting.length > 1 ? 's' : ''} de montant existant</strong> — des résas ont déjà un montant saisi ≠ du CSV.
                  </div>
                )}
                {!facturesLues && (
                  <div data-testid="csv-factures-pas-lues" style={{background:"#fff3e0",border:`1px solid ${DS.amber}55`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:"#555",lineHeight:1.5}}>
                    {TEXTE_FACTURES_PAS_LUES}
                  </div>
                )}
                <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:6}}>
                  <button onClick={close} disabled={applying}
                    style={{flex:"0 0 auto",background:"#eef2f7",color:DS.navy,border:"none",borderRadius:10,padding:"11px 16px",fontSize:12,fontWeight:700,cursor:applying?"wait":"pointer",fontFamily:DS.fontBody}}>
                    Annuler
                  </button>
                  <button onClick={handleApply} disabled={applying || !facturesLues}
                    style={{flex:1,background:applying?"#aaa":DS.navy,color:"#fff",border:"none",borderRadius:10,padding:"11px 16px",fontSize:12,fontWeight:700,cursor:applying?"wait":"pointer",fontFamily:DS.fontBody}}>
                    {applying ? '⏳ Application...' : `✅ Confirmer l'import`}
                  </button>
                </div>
              </>
            )}

            {/* Succès */}
            {done && (
              <div style={{background:"#edf7f0",border:`1px solid ${DS.green}44`,borderRadius:10,padding:12,marginBottom:10}}>
                <div style={{fontSize:13,fontWeight:700,color:DS.green,marginBottom:4}}>✅ Import effectué</div>
                <div style={{fontSize:11,color:"#444",marginBottom:10}}>
                  {done.created} créée{done.created > 1 ? 's' : ''}, {done.updated} mise{done.updated > 1 ? 's' : ''} à jour, {done.ignored} ignorée{done.ignored > 1 ? 's' : ''}.
                </div>
                <button onClick={close} style={{background:DS.navy,color:"#fff",border:"none",borderRadius:8,padding:"8px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:DS.fontBody}}>Fermer</button>
              </div>
            )}

            {/* Erreur */}
            {error && (
              <div style={{background:"#fdf2f2",border:`1px solid ${DS.red}44`,borderRadius:10,padding:10,marginBottom:10,fontSize:11,color:DS.red,fontWeight:600}}>
                ❌ {error}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
